import { DEFAULT_LANGUAGE, type Language } from './languages';

/** Platform-neutral persistence boundary (spec §2). Web: localStorage; iOS: Capacitor Preferences. */
export interface StorageAdapter {
  get(key: string): Promise<string | null>;
  set(key: string, value: string): Promise<void>;
  remove(key: string): Promise<void>;
}

/** In-memory adapter for tests. */
export class MemoryStorage implements StorageAdapter {
  private map = new Map<string, string>();
  async get(key: string): Promise<string | null> {
    return this.map.get(key) ?? null;
  }
  async set(key: string, value: string): Promise<void> {
    this.map.set(key, value);
  }
  async remove(key: string): Promise<void> {
    this.map.delete(key);
  }
}

export const KEYS = {
  profile: 'lexicon.profile',
  stats: 'lexicon.stats',
  quotas: 'lexicon.quotas',
  dailyGame: 'lexicon.dailyGame',
  deviceId: 'lexicon.deviceId',
  myChallenges: 'lexicon.myChallenges',
} as const;

/**
 * The storage key for `key` under `language`.
 *
 * The default language keeps the bare key it has always used, so an
 * existing player's streak, XP, stats and in-progress daily are exactly
 * where they were — adding Dutch migrates nothing. Every other language
 * gets its own suffixed key and therefore its own, independent progress
 * (spec §10.2: a day's puzzle in Dutch is a different puzzle, so a Dutch
 * win cannot count towards an English streak).
 *
 * Not everything is scoped, and the split is deliberate:
 *
 * - Per language — `profile` (XP + streak), `stats`, the daily and journey
 *   sessions, journey progress. All of these are records *of playing this
 *   language's puzzles*.
 * - Shared — `deviceId` (one install), `quotas` (2 hints and 3 insights
 *   per day *per player*; scoping them would hand out a fresh budget for
 *   the price of a language switch), `myChallenges` and per-code challenge
 *   sessions (a challenge is identified by its code and carries its own
 *   word, whatever language the device is set to).
 */
export function scopedKey(key: string, language: Language = DEFAULT_LANGUAGE): string {
  return language === DEFAULT_LANGUAGE ? key : `${key}.${language}`;
}

/** Parse stored JSON; any corruption yields the fallback (spec §7). */
export async function readJson<T>(s: StorageAdapter, key: string, fallback: T): Promise<T> {
  const raw = await s.get(key);
  if (raw === null) return fallback;
  try {
    const parsed: unknown = JSON.parse(raw);
    // JSON "null" and bare primitives (numbers, strings, booleans) are not
    // the shape any caller expects; treat them the same as missing data.
    // Arrays pass (typeof [] === 'object'), which is fine for list values.
    if (parsed === null || typeof parsed !== 'object') return fallback;
    return parsed as T;
  } catch {
    return fallback;
  }
}

/** Serialize and store a value under `key`. */
export async function writeJson<T>(s: StorageAdapter, key: string, value: T): Promise<void> {
  await s.set(key, JSON.stringify(value));
}
